import React, { Fragment } from 'react'

import {
  AlignItems,
  Box,
  Color,
  type FontSize,
  Display,
  FlexDirection,
  GridDisplay,
  JustifyContent,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

export type PriceRow = {
  /** The price before the first-box discount, shown struck through. */
  originalPrice: string
  /** The price the customer pays. */
  currentPrice: string
}

export type DeliveryPriceRow = PriceRow & {
  /** Free delivery is called out in the success colour rather than shown as an amount. */
  currentPriceIsFree: boolean
}

export type PriceBreakdownCardProps = {
  boxLabel: string
  discountBadgeLabel: string
  recipesLabel: string
  recipesPrice: PriceRow
  deliveryPrice: DeliveryPriceRow
  totalPrice: PriceRow
}

// The shadow is Elevation/50's own token math, not Figma's exported approximation of it.
const CardSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  boxShadow: '0 0 2px rgba(33, 41, 48, 0.05), 0 1px 4px rgba(33, 41, 48, 0.1)',
}))

/**
 * One grid for every row, so the struck and current prices each share a column across the whole
 * card. Laying rows out independently would only align for one particular set of values.
 *
 * Baseline, not centre: every row pairs two font sizes, which centring leaves visibly offset.
 *
 * The price columns are fixed rather than `max-content`, whose width tracks its current value —
 * the card would shift sideways as the selection changed the length of the prices. 58/64px fit
 * every string up to "£99.99", the ceiling for this pricing model.
 */
const PriceGrid = withCitrusTheme(Box, () => ({
  display: 'grid',
  gridTemplateColumns: '1fr 58px 64px',
  alignItems: 'baseline',
  columnGap: '8px',
  rowGap: '8px',
}))

const RowDivider = withCitrusTheme(Box, () => ({
  gridColumn: '1 / -1',
  height: '1px',
}))

const DiscountBadge = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
}))

type PriceCellsProps = {
  testIdPrefix: string
  price: PriceRow
  originalSize: FontSize
  currentSize: FontSize
  currentColor: Color
}

const PriceCells = ({
  testIdPrefix,
  price,
  originalSize,
  currentSize,
  currentColor,
}: PriceCellsProps) => (
  <Fragment>
    {/*
      A real <s> element, not a line-through style: the meaning "this price no longer applies"
      then survives high-contrast modes and plain-text extraction, where the decoration would not.
      The columns are a fixed width shared across every row (see PriceGrid), so a shorter value
      such as "FREE" sits in a cell wider than its own content — textAlign keeps it flush with
      the prices above and below rather than floating at the cell's left edge.
    */}
    <s data-testid={`${testIdPrefix}-original`}>
      <Text size={originalSize} color={Color.ColdGrey_600} textAlign={TextAlign.Right}>
        {price.originalPrice}
      </Text>
    </s>
    <Text
      data-testid={`${testIdPrefix}-current`}
      size={currentSize}
      color={currentColor}
      fontWeight={700}
      textAlign={TextAlign.Right}
    >
      {price.currentPrice}
    </Text>
  </Fragment>
)

export const PriceBreakdownCard = ({
  boxLabel,
  discountBadgeLabel,
  recipesLabel,
  recipesPrice,
  deliveryPrice,
  totalPrice,
}: PriceBreakdownCardProps) => (
  <CardSurface
    bg={Color.White}
    paddingV={[4, null, null, 3]}
    paddingH={4}
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={[6, null, null, 4]}
  >
    <Box
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.SpaceBetween}
      gap={2}
    >
      <Text size={5} fontWeight={700} color={Color.ColdGrey_800}>
        {boxLabel}
      </Text>
      <DiscountBadge bg={Color.Success_900} paddingH={2} paddingV={0.5}>
        <Text size={2} fontWeight={700} color={Color.White}>
          {discountBadgeLabel}
        </Text>
      </DiscountBadge>
    </Box>

    <PriceGrid data-testid="price-breakdown-grid" display={GridDisplay.Grid}>
      <Text size={2} color={Color.ColdGrey_800}>
        {recipesLabel}
      </Text>
      <PriceCells
        testIdPrefix="recipes"
        price={recipesPrice}
        originalSize={2}
        currentSize={4}
        currentColor={Color.ColdGrey_800}
      />

      <RowDivider bg={Color.ColdGrey_50} />

      <Text size={2} color={Color.ColdGrey_800}>
        Delivery
      </Text>
      <PriceCells
        testIdPrefix="delivery"
        price={deliveryPrice}
        originalSize={2}
        currentSize={4}
        currentColor={deliveryPrice.currentPriceIsFree ? Color.Success_900 : Color.ColdGrey_800}
      />

      <RowDivider bg={Color.ColdGrey_50} />

      <Text size={4} fontWeight={700} color={Color.ColdGrey_800}>
        Total
      </Text>
      <PriceCells
        testIdPrefix="total"
        price={totalPrice}
        originalSize={4}
        currentSize={6}
        currentColor={Color.Success_900}
      />
    </PriceGrid>
  </CardSurface>
)
